import React, { FC, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import FeatherIcon from "feather-icons-react";
import { useSnackbar } from "notistack";
import { unparse } from "papaparse";
import {
  DoubleMaterialityValueChainPart,
  type AssessmentDto,
} from "@green0meter/api-client";
import {
  DataGrid,
  GridColDef,
  GridFilterItem,
  getGridSingleSelectOperators,
  GridToolbarContainer,
  GridToolbarFilterButton,
  GridToolbarQuickFilter,
} from "@mui/x-data-grid";

import DeleteConfirmationDialog from "../../../DeleteConfirmationDialog/DeleteConfirmationDialog";
import ESRSAreaChip from "../../../ESRSAreaChip/ESRSAreaChip";
import { ESRS_CODE_BY_INDEX } from "./ESRS_CODE_BY_INDEX";
import { useESRSTopics } from "../AssessmentEditor/useESRSTopics";
import { useValueChainLabels } from "../AssessmentEditor/AssessmentEditor.utils";
import type {
  Subtopic,
  SubSubTopic,
} from "../AssessmentEditor/AssessmentEditor.utils";

import { IconButton, Stack, Button, Box, Chip, Typography } from "@components/base";
import { useUserStore } from "@store/user";
import {
  useDeleteAssessment,
  useGetAssessmentQuestionCount,
  useGetAssessments,
} from "@api";
import { getYearFromSearchParams } from "@helpers";

interface IQualitativeAssessmentTable {
  onAssessmentEdit?: (assessment: AssessmentDto) => void;
  onAddAssessment?: () => void;
}

const QualitativeAssessmentTable: FC<IQualitativeAssessmentTable> = ({
  onAssessmentEdit,
  onAddAssessment,
}) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useUserStore();
  const [searchParams] = useSearchParams();
  const year = getYearFromSearchParams(searchParams);

  const { ESRS_TOPICS, getSubtopicsForTopic, getSubSubtopicsForTopic } = useESRSTopics();
  const { getValueChainLabel } = useValueChainLabels();

  const getValueChainSortValue = (
    valueChainParts: DoubleMaterialityValueChainPart[] | null | undefined,
  ): string =>
    (valueChainParts ?? [])
      .map((part) => getValueChainLabel(part))
      .sort((a, b) => a.localeCompare(b))
      .join(", ");

  const valueChainFilterOperators = getGridSingleSelectOperators()
    .filter((operator) => operator.value === "is" || operator.value === "not")
    .map((operator) => ({
      ...operator,
      getApplyFilterFn: (filterItem: GridFilterItem) => {
        if (!filterItem.field || !filterItem.value || !filterItem.operator) {
          return null;
        }

        return (value: DoubleMaterialityValueChainPart[]) => {
          if (!Array.isArray(value)) return false;
          if (operator.value === "is") {
            return value.includes(filterItem.value as DoubleMaterialityValueChainPart);
          }
          if (operator.value === "not") {
            return !value.includes(filterItem.value as DoubleMaterialityValueChainPart);
          }

          return false;
        };
      },
    }));

  const getSubTopicDisplayName = (
    topic: number,
    subTopicCode: string | null | undefined,
  ): string | null => {
    if (!subTopicCode || subTopicCode.trim() === "") return null;
    const subtopics = getSubtopicsForTopic(topic);
    const subtopic = subtopics.find((st: Subtopic) => st.code === subTopicCode);

    return subtopic?.name ?? null;
  };

  const getSubSubTopicDisplayName = (
    topic: number,
    subTopicCode: string | null | undefined,
    subSubTopicCode: string | null | undefined,
  ): string | null => {
    if (
      !subSubTopicCode ||
      subSubTopicCode.trim() === "" ||
      !subTopicCode ||
      subTopicCode.trim() === ""
    )
      return null;
    const subSubTopics = getSubSubtopicsForTopic(topic, subTopicCode);
    const subSubTopic = subSubTopics.find(
      (sst: SubSubTopic) => sst.code === subSubTopicCode,
    );

    return subSubTopic?.name ?? null;
  };

  const { assessments, isPendingGetAssessments } = useGetAssessments(user, {
    year,
    pageSize: 1000,
    pageNumber: 1,
  });
  const topicFilterOptions = useMemo(
    () =>
      Array.from(
        new Set(
          (assessments?.data ?? [])
            .map((assessment) => ESRS_CODE_BY_INDEX[assessment.topic] ?? "")
            .filter(Boolean),
        ),
      )
        .sort((a, b) => a.localeCompare(b))
        .map((code) => ({
          value: code,
          label: code === "OTHER" ? ESRS_TOPICS[code]?.name ?? code : `${code}: ${ESRS_TOPICS[code]?.name ?? code}`,
        })),
    [assessments?.data, ESRS_TOPICS],
  );
  const subTopicFilterOptions = useMemo(
    () =>
      Array.from(
        new Set(
          (assessments?.data ?? [])
            .map((assessment) =>
              getSubTopicDisplayName(assessment.topic, assessment.subTopic) ?? "",
            )
            .filter(Boolean),
        ),
      ).sort((a, b) => a.localeCompare(b)),
    [assessments?.data, getSubtopicsForTopic],
  );
  const subSubTopicFilterOptions = useMemo(
    () =>
      Array.from(
        new Set(
          (assessments?.data ?? [])
            .map((assessment) =>
              getSubSubTopicDisplayName(
                assessment.topic,
                assessment.subTopic,
                assessment.subSubTopic,
              ) ?? "",
            )
            .filter(Boolean),
        ),
      ).sort((a, b) => a.localeCompare(b)),
    [assessments?.data, getSubSubtopicsForTopic],
  );

  const handleAssessmentEdit = (assessment: AssessmentDto): void => {
    onAssessmentEdit?.(assessment);
  };

  const {
    deleteAssessmentAsync,
    isPendingDeleteAssessment,
    isSuccessDeleteAssessment,
  } = useDeleteAssessment(user);
  const { getAssessmentQuestionCountAsync, isPendingAssessmentQuestionCount } =
    useGetAssessmentQuestionCount(user);

  const [assessmentToDelete, setAssessmentToDelete] = useState<AssessmentDto | null>(
    null,
  );
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [linkedQuestionsCount, setLinkedQuestionsCount] = useState<number | null>(
    null,
  );

  const handleExportCSV = (): void => {
    if (!assessments?.data.length) {
      enqueueSnackbar(t("assessments.export.noData", "No data to export"), {
        variant: "warning",
      });

      return;
    }

    const stripHtml = (html: unknown): string => {
      if (typeof html !== "string") return "";
      const doc = new DOMParser().parseFromString(html, "text/html");

      return doc.body.textContent || "";
    };

    const csvData = assessments.data.map((assessment) => ({
      "Assessment ID": assessment.assessmentId,
      Topic: ESRS_CODE_BY_INDEX[assessment.topic],
      "Sub Topic": assessment.subTopic,
      "Sub-Sub Topic": assessment.subSubTopic,
      "Sustainability Matter": assessment.sustainabilityMatter,
      Impacts: stripHtml(assessment.impacts),
      Risks: stripHtml(assessment.risks),
      Opportunities: stripHtml(assessment.opportunities),
      "Value Chain": assessment.partOfValueChain.join(", "),
      "Relevant subjects":
        assessment.relevantSubjects?.map((subject) => subject.name).join(", ") || "",
      Sources: assessment.sources,
      Year: assessment.year,
    }));

    const csv = unparse(csvData);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");

    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);

      link.setAttribute("href", url);
      link.setAttribute(
        "download",
        `assessments-${new Date().toISOString().split("T")[0]}.csv`,
      );
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      enqueueSnackbar(t("assessments.export.success", "Export successful"), {
        variant: "success",
      });
    }
  };

  const handleAssessmentDelete = (assessment: AssessmentDto): void => {
    setAssessmentToDelete(assessment);
    setShowDeleteDialog(true);
    setLinkedQuestionsCount(null);
    void getAssessmentQuestionCountAsync(assessment.id)
      .then((result) => {
        setLinkedQuestionsCount(result.questionCount);
      })
      .catch(() => {
        setLinkedQuestionsCount(null);
      });
  };

  const handleDeleteConfirm = async (): Promise<void> => {
    if (!assessmentToDelete) return;
    const deleteQuestions =
      linkedQuestionsCount === null ? true : (linkedQuestionsCount ?? 0) > 0;

    await deleteAssessmentAsync({
      id: assessmentToDelete.id,
      deleteQuestions,
    });
  };

  const handleDeleteCancel = (): void => {
    setShowDeleteDialog(false);
    setAssessmentToDelete(null);
    setLinkedQuestionsCount(null);
  };

  useEffect(() => {
    if (isSuccessDeleteAssessment) {
      setShowDeleteDialog(false);
      setAssessmentToDelete(null);
      setLinkedQuestionsCount(null);
    }
  }, [isSuccessDeleteAssessment]);

  const renderESRSChips = (esrsAreas: string[]): JSX.Element => (
    <Stack direction="row" spacing={1.25} flexWrap="wrap" useFlexGap>
      {esrsAreas.map((area) => (
        <Chip key={area} label={area} color="primary" size="small" />
      ))}
    </Stack>
  );

  const renderHtmlCell = (html?: string): JSX.Element => (
    <Box
      sx={{
        maxWidth: 320,
        overflow: "hidden",
      }}
    >
      <Typography
        component="div"
        variant="body2"
        sx={{
          display: "-webkit-box",
          WebkitBoxOrient: "vertical",
          WebkitLineClamp: 5,
          overflow: "hidden",
          textOverflow: "ellipsis",
          "& p": { m: 0 },
        }}
      >
        <div dangerouslySetInnerHTML={{ __html: html ?? "" }} />
      </Typography>
    </Box>
  );

  const columns: GridColDef<AssessmentDto>[] = [
    {
      field: "assessmentId",
      headerName: "Assessment ID",
      width: 150,
    },
    {
      field: "topic",
      headerName: "Topic",
      width: 100,
      type: "singleSelect",
      valueOptions: topicFilterOptions,
      valueGetter: (value) => {
        if (value === null || value === undefined) return "";

        return ESRS_CODE_BY_INDEX[value] || "";
      },
      renderCell: (params) => {
        if (!params.value) return null;

        return <ESRSAreaChip label={params.value} uppercase />;
      },
    },
    {
      field: "subTopic",
      headerName: "Sub-topic",
      width: 150,
      type: "singleSelect",
      valueOptions: subTopicFilterOptions,
      valueGetter: (value, row) => {
        return getSubTopicDisplayName(row.topic, value) || "";
      },
      renderCell: (params) => {
        return params.value ? (
          <Typography variant="body2" title={params.value as string}>{params.value}</Typography>
        ) : (
          <Typography variant="body2" color="text.secondary">
            {"-"}
          </Typography>
        );
      },
    },
    {
      field: "subSubTopic",
      headerName: "Sub-sub-topic",
      width: 150,
      type: "singleSelect",
      valueOptions: subSubTopicFilterOptions,
      valueGetter: (value, row) => {
        return getSubSubTopicDisplayName(row.topic, row.subTopic, value) || "";
      },
      renderCell: (params) => {
        return params.value ? (
          <Typography variant="body2" title={params.value as string}>{params.value}</Typography>
        ) : (
          <Typography variant="body2" color="text.secondary">
            {"-"}
          </Typography>
        );
      },
    },
    {
      field: "sustainabilityMatter",
      headerName: "Sustainability Matter",
      width: 250,
    },
    {
      field: "partOfValueChain",
      headerName: "Part of the value chain",
      width: 280,
      type: "singleSelect",
      valueOptions: Object.values(DoubleMaterialityValueChainPart).map((part) => ({
        value: part,
        label: getValueChainLabel(part),
      })),
      valueGetter: (value: DoubleMaterialityValueChainPart[]) => value,
      sortComparator: (valueA, valueB) =>
        getValueChainSortValue(
          valueA as DoubleMaterialityValueChainPart[] | undefined,
        ).localeCompare(
          getValueChainSortValue(
            valueB as DoubleMaterialityValueChainPart[] | undefined,
          ),
        ),
      filterOperators: valueChainFilterOperators,
      renderCell: (params) =>
        renderESRSChips((params.value as unknown as string[]) ?? []),
    },
    {
      field: "impacts",
      headerName: "Impacts",
      width: 320,
      renderCell: (params) =>
        renderHtmlCell(params.value as unknown as string),
    },
    {
      field: "risks",
      headerName: "Risks",
      width: 320,
      renderCell: (params) =>
        renderHtmlCell(params.value as unknown as string),
    },
    {
      field: "opportunities",
      headerName: "Opportunities",
      width: 320,
      renderCell: (params) =>
        renderHtmlCell(params.value as unknown as string),
    },
    {
      field: "sources",
      headerName: "Sources",
      width: 320,
      renderCell: (params) =>
        renderHtmlCell(params.value as unknown as string),
    },
    {
      field: "actions",
      headerName: t("assessments.table.actions", "Actions"),
      width: 120,
      align: "center",
      headerAlign: "center",
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Stack direction="row" spacing={1} justifyContent="center" width="100%">
          <IconButton
            onClick={() => handleAssessmentEdit(params.row)}
            color="primary"
            size="small"
          >
            <FeatherIcon icon="edit-3" size={16} />
          </IconButton>
          <IconButton
            onClick={() => handleAssessmentDelete(params.row)}
            color="error"
            size="small"
          >
            <FeatherIcon icon="trash" size={16} />
          </IconButton>
        </Stack>
      ),
    },
  ];

  if (isPendingGetAssessments) {
    return <div>{t("common.loading")}</div>;
  }

  const hasLinkedQuestions = (linkedQuestionsCount ?? 0) > 0;
  const deleteDescription = hasLinkedQuestions
    ? t(
      "assessments.deleteDialog.withQuestions",
      "This assessment is referenced by {{count}} question(s). Deleting it will remove those questions as well. Do you want to proceed?",
      { count: linkedQuestionsCount ?? 0 },
    )
    : t(
      "assessments.deleteDialog.message",
      "Are you sure you want to remove this assessment? This action cannot be undone.",
    );


  // Custom Toolbar
  const CustomToolbar = (): JSX.Element => {
    return (
      <GridToolbarContainer
        sx={{
          display: "flex",
          justifyContent: "space-between",
          p: 2,
        }}
      >
        <Stack direction="row" spacing={2} alignItems="center">
          <GridToolbarQuickFilter />
          <GridToolbarFilterButton />
        </Stack>
        <Stack direction="row" spacing={1}>
          <Button
            variant="text"
            startIcon={<FeatherIcon icon="plus" size={16} />}
            onClick={onAddAssessment}
            sx={{
              textTransform: "none",
              fontWeight: 600,
            }}
          >
            {t("assessments.addAssessment", "Add assessment")}
          </Button>

          <Button
            variant="text"
            startIcon={<FeatherIcon icon="download" size={16} />}
            onClick={handleExportCSV}
            sx={{
              textTransform: "none",
              fontWeight: 600,
            }}
          >
            {t("assessments.export.title", "Export")}
          </Button>
        </Stack>
      </GridToolbarContainer>
    );
  };

  return (
    <Box>
      <DataGrid
        rows={assessments?.data ?? []}
        columns={columns}
        loading={isPendingGetAssessments}
        hideFooterPagination
        slots={{
          toolbar: CustomToolbar,
          noRowsOverlay: () => (
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%", p: 2 }}>
              <Typography>{t("common.noData", "No data available")}</Typography>
            </Box>
          )
        }}
        slotProps={{
          panel: {
            sx: {
              "& .MuiDataGrid-filterForm": {
                display: "flex",
                flexDirection: "row",
                flexWrap: "nowrap",
                alignItems: "flex-end",
                gap: 2,
                p: 2,
              },
              "& .MuiDataGrid-filterFormDeleteIcon": {
                order: -1,
                marginBottom: 0.5,
              },
              "& .MuiDataGrid-filterFormColumnInput": {
                minWidth: 150,
              },
              "& .MuiDataGrid-filterFormOperatorInput": {
                minWidth: 120,
              },
              "& .MuiDataGrid-filterFormValueInput": {
                minWidth: 220,
                "& label": {
                  visibility: "hidden",
                },
                "& input.MuiInputBase-input": {
                  paddingBottom: "24px",
                },
              },
            },
          },
        }}
        getRowHeight={() => 'auto'}
        sx={{
          border: 0,
          '& .MuiDataGrid-cell': {
            display: 'flex',
            alignItems: 'center',
            py: 1
          },
          "& .MuiDataGrid-columnHeader": {
            backgroundColor: "#f5f5f5",
          },
        }}
        disableRowSelectionOnClick
      />

      <DeleteConfirmationDialog
        open={showDeleteDialog}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        title={t("assessments.deleteDialog.title", "Confirm delete")}
        description={deleteDescription}
        details={
          assessmentToDelete ? (
            <>
              <strong>{assessmentToDelete.assessmentId}</strong>
            </>
          ) : undefined
        }
        isLoading={isPendingDeleteAssessment || isPendingAssessmentQuestionCount}
        confirmDisabled={
          isPendingDeleteAssessment || isPendingAssessmentQuestionCount
        }
        confirmText={t("common.remove")}
      />
    </Box >
  );
};

export default QualitativeAssessmentTable;
